import { useState, useCallback, isValidElement, cloneElement } from 'react'
import { Avatar, Menu, MenuItem, AppBar, Toolbar, IconButton, Typography, Divider, Drawer, 
        List, ListItem, ListItemIcon, ListItemText, Accordion, AccordionSummary, 
        AccordionDetails, Tooltip } from '@mui/material'
import { ExpandMore, MenuOutlined, Settings, Logout, Api, AccountCircle, SearchOff } from '@mui/icons-material'

import { D, C, openLink } from '../include'
import { navigation } from '../navigation'

const BACKGROUNDCOLOR = '#3C4252'   // background color do sidebar e faixa no topo

// ==========================================
// COMPONENTES INTERNOS (Sem export)
// ==========================================

const NoPage = () => {
  const attemptedUrl = window.location.pathname;
  
  return (
    <D className="col e aC" style={{ justifyContent: 'center', height: '100%', width: '100%' }}>
      <SearchOff sx={{ fontSize: 80, color: '#999', mb: 2 }} />
      <Typography variant="h4" color="textSecondary" gutterBottom>
        Página Não Encontrada
      </Typography>
      <Typography variant="body1" color="textSecondary">
        A rota solicitada não existe em nosso sistema.
      </Typography>
      <Typography variant="caption" color="textSecondary" style={{ marginTop: '1rem' }}>
        URL: <strong>{attemptedUrl}</strong>
      </Typography>
    </D>
  );
};

const SideBar = ({ user, closeMenu, changeView }) => {
  const userPerms = user?.permissions || [];

  return (
    <>
      <Toolbar sx={{ backgroundColor: BACKGROUNDCOLOR }}>
        <D className="row e aC">
          <D className="row e aC">
            <img alt="Logo" src={C.navIMG} style={{ maxHeight: '28px' }} />
            <Typography variant="subtitle1" component="div" sx={{ color: '#fff', flexGrow: 1 }}>{C.incNAME}</Typography>
          </D>
          <Tooltip title="Esconder Menu" arrow>          
            <IconButton size="large" edge="end" color="inherit" aria-label="fechar" onClick={closeMenu}>
              <MenuOutlined style={{ color: '#fff' }} />
            </IconButton>
          </Tooltip> 
        </D>
      </Toolbar>

      <Divider />

      <List className="col e aF scY">
        {navigation.map((n) => {
          // Se o item tem permissao publica ('*') lista anyway
          const hasPermission = 
              n.permission === '*' ||
              userPerms.includes(n.permission) ||
              userPerms.some(p => p.startsWith(n.title + '-'));

          if (!hasPermission) return null; // se nao tem permissao nao apresenta

          // -------- ITEM PRINCIPAL --------
          if (n.type === 'item' || n.type === 'link') {
            return (
              <ListItem
                button
                key={n.id}
                onClick={() => n.type === 'link' ? openLink(n.url) : changeView(n.url)}
              >
                <ListItemIcon>{n.icon}</ListItemIcon>
                <ListItemText primary={n.title} sx={{ color: '#fff' }} />
              </ListItem>
            );
          }

          // -------- COLLAPSE --------
          if (n.type === 'collapse') {
            return (
              <Accordion
                key={n.id}
                sx={{
                  boxShadow: 0,
                  backgroundColor: 'transparent',
                  '::before': { backgroundColor: 'transparent' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#ccc' }} />}>
                  <ListItemIcon>{n.icon}</ListItemIcon>
                  <Typography sx={{ color: '#fff' }}>{n.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {n.children.map((c) => {
                    const hasChildPermission =
                      c.permission === '*' ||
                      userPerms.includes(c.permission) ||
                      userPerms.some(p => p.startsWith(c.title + '-'));

                    if (!hasChildPermission) return null;

                    if (c.type === 'item' || c.type === 'link') {
                      return (
                        <ListItem
                          button
                          key={c.id}
                          onClick={() => c.type === 'link' ? openLink(c.url) : changeView(c.url)}
                        >
                          <ListItemIcon>{c.icon}</ListItemIcon>
                          <ListItemText primary={c.title} sx={{ color: '#fff' }} />
                        </ListItem>
                      );
                    }
                    return null;
                  })}
                </AccordionDetails>
              </Accordion>
            );
          }
          return null;
        })}
      </List>

      <D className="row w aC hv"
        style={{ 
          minHeight: '75px', 
          maxHeight: '75px', 
          color: '#fff', 
          cursor: 'pointer', 
          gap: '7px' 
        }}
        onClick={() => openLink('http://ai2bizlab.com.br')}>
        {'\u00A9 '}<b>{C.appNAME}</b> by
        <img style={{ maxHeight: '24px' }} src="/static/ai2bizlab.png" alt="AI2 Logo" />Business
      </D>
    </>
  );
};


// Resolve a view sem depender de estado do React
const getResolvedView = (targetUrl, currentUser) => {
  // Se a URL for raiz, adota mediateca como default
  let u = targetUrl === '/' ? '/mediateca' : targetUrl;
  
  let vw = {
    id: 'nopage',
    title: 'Página não encontrada',
    type: 'route',
    icon: null,
    component: <NoPage />,
    url: '/nopage',
    permission: ''
  };

  const userPerms = currentUser?.permissions || [];

  for (const v of Object.values(navigation)) {
    if (v.type !== 'collapse' && v.url === u) {
      if (v.permission === '*' || userPerms.includes(v.permission)) {
        vw = { ...v, title: v.title };
      }
    } else if (v.type === 'collapse') {
      for (const v2 of Object.values(v.children)) {
        if (v2.type !== 'collapse' && v2.url === u) {
          if (v2.permission === '*' || userPerms.includes(v2.permission)) {
            vw = { ...v2, title: `${v.title} - ${v2.title}` };
          }
        }
      }
    }
  }

  // Resolve a chave funcional correta
  const permissionKey = vw.permission && vw.permission !== '*'
    ? vw.permission.replace(/-(Ler|Escrever)$/, '')
    : vw.title;

  // 1️⃣ Usuário tem permissões no profile
  if (userPerms.length > 0) {
    if (userPerms.includes(`${permissionKey}-Escrever`)) {
      vw.perm = 'w';
    } else if (userPerms.includes(`${permissionKey}-Ler`)) {
      vw.perm = 'r';
    } else {
      vw.perm = null;
    }
  // 2️⃣ Usuário NÃO tem permissões e item é público (*)
  } else if (vw.permission === '*') {
    vw.perm = 'r';
  // 3️⃣ Caso contrário → sem permissão
  } else {
    vw.perm = null;
  }

  return vw;
};


// ==========================================
// COMPONENTE PRINCIPAL: MainPage
// ==========================================

export const MainPage = (props) => {
  const { user } = props;

  const [menuOpen, setMenuOpen] = useState(true);
  const [menuOpenSM, setMenuOpenSM] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  
  // 🔥 Lazy Initialization: O state já nasce calculando a página correta, evitando o useEffect.
  const [view, setView] = useState(() => {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
    return getResolvedView(currentPath, user);
  });

  // changeView agora é super leve e apenas chama a função pura.
  const changeView = useCallback((u) => {
    const resolved = getResolvedView(u, user);
    setView(resolved);
    setMenuOpenSM(false);
  }, [user]);

  return (
    <D className="row e aF scD">
      
      {/* Drawer Desktop */}
      <D className="hide-md">
        <Drawer 
          sx={menuOpen ? { 
            width: '300px', minHeight: '0px', minWidth: '0px', 
            '& .MuiDrawer-paper': { backgroundColor: BACKGROUNDCOLOR, width: '300px' } 
          } : {}}
          variant="persistent" 
          ModalProps={{ keepMounted: true }} 
          anchor="left" open={menuOpen}
        >
          <SideBar 
            user={user} 
            closeMenu={() => setMenuOpen(false)} 
            changeView={changeView} 
          />
        </Drawer>
      </D>

      {/* Drawer Mobile */}
      <D className="hide unhide-md">
        <Drawer 
          sx={{ minHeight: '0px', minWidth: '0px', '& .MuiDrawer-paper': { backgroundColor: BACKGROUNDCOLOR } }} 
          anchor="left" 
          open={menuOpenSM} 
          onClose={() => setMenuOpenSM(false)}
        >
          <SideBar 
            user={user} 
            closeMenu={() => setMenuOpenSM(false)} 
            changeView={changeView} 
          />
        </Drawer>
      </D>

      <D className="col e aF scD">
        <AppBar position="static">
          <Toolbar sx={{ backgroundColor: BACKGROUNDCOLOR }}>
            
            {!menuOpen && (
              <D className="row aC hide-md">
                <Tooltip title="Apresentar Menu" arrow>
                  <IconButton 
                    size="large" edge="start" color="inherit" aria-label="menu" 
                    sx={{ mr: 2 }} onClick={() => setMenuOpen(true)}>
                    <MenuOutlined />
                  </IconButton>
                </Tooltip>
                <img alt="Logo" src={C.navIMG} style={{ maxHeight: '28px' }} />
                <Typography variant="subtitle1" component="div" sx={{ color: '#fff', flexGrow: 1 }}>Wilson Sons</Typography>
                <D style={{ marginLeft: '35px' }} />
              </D>
            )}

            <D className="row aC hide unhide-md">
              <IconButton 
                size="large" edge="start" color="inherit" aria-label="menu" 
                sx={{ mr: 2 }} onClick={() => setMenuOpenSM(true)}>
                <MenuOutlined />
              </IconButton>
            </D>

            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {view?.title || ''}
            </Typography>

            <D className="row aC">
              <D className="col aC hide-md">
                <Typography variant="h6" component="div">{user?.name}</Typography>
              </D>
              <IconButton 
                onClick={e => setProfileMenuAnchor(e.currentTarget)} 
                aria-expanded={Boolean(profileMenuAnchor)}
                size="small" sx={{ ml: 2 }} 
                aria-controls={Boolean(profileMenuAnchor) ? 'account-menu' : undefined}
              >
                {user?.photo ? (
                  <img alt="Perfil" src={`${C.baseURL}pic/${user.photo}`} style={{ width: '32px', height: '32px', borderRadius: '50%' }} /> 
                ) : (
                  <Avatar sx={{ width: 32, height: 32 }}>{user?.name?.[0]}</Avatar>
                )}
              </IconButton>
            </D>

            <Menu
              anchorEl={profileMenuAnchor}
              id="account-menu"
              open={Boolean(profileMenuAnchor)}
              onClose={() => setProfileMenuAnchor(null)}
              onClick={() => setProfileMenuAnchor(null)}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '& .MuiAvatar-root': { width: 32, height: 32, ml: -0.5, mr: 1 },
                  '&:before': {
                    content: '""',
                    display: 'block', position: 'absolute', top: 0, right: 14,
                    width: 10, height: 10, bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)', zIndex: 0,
                  }
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {/* Identificação */}
              <D className="col aC" style={{ padding: '0.5rem' }}>
                <AccountCircle fontSize="small" />
                <Typography variant="subtitle1" component="div">{user?.email}</Typography>
                <Typography sx={{ fontWeight: 'light', textAlign: 'center' }} variant="subtitle2" component="div">{user?.role}</Typography>

                <D h="1rem" />
                <Divider style={{ width: '100%' }} />
                <D h="0.5rem" />

                <Api fontSize="small" />
                <Typography sx={{ textAlign: 'center' }} variant="h6" component="div">{C.appNAME}</Typography>
                <Typography sx={{ fontWeight: 'light', textAlign: 'center' }} variant="subtitle2" component="div">{C.appVERSION}</Typography>
                
                <D h="0.5rem" />
                <Divider style={{ width: '100%' }} />
              </D>

              {/* Menus */}
              <MenuItem onClick={() => changeView('/profile')}>
                <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
                Meu Cadastro
              </MenuItem>

              <MenuItem onClick={() => props.logout('LOG')}>
                <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                Sair
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* View Component Injector */}
        <D className="row e aF scD">
          {view === null ? null : (
            view.component && isValidElement(view.component) 
              ? cloneElement(view.component, { ...props, permission: view.perm }) 
              : <NoPage />
          )}
        </D>
      </D>
    </D>
  );
};
